/**
 * Dutch formatting helpers for the upload progress UI.
 * Kept separate from the queue so both the panel and any future surface
 * (notifications, mobile sheet) format identically.
 */

const BYTE_UNITS = ["B", "KB", "MB", "GB"];

/** e.g. 4,2 MB - Dutch decimal comma, no unit for 0. */
export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    value /= 1024;
    unitIndex++;
  }

  // Bytes and KB read better without decimals; MB and up get one.
  const decimals = unitIndex <= 1 ? 0 : 1;
  return `${value.toFixed(decimals).replace(".", ",")} ${BYTE_UNITS[unitIndex]}`;
};

/** e.g. 3,4 MB/s */
export const formatThroughput = (bytesPerSecond: number): string => {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return "-";
  return `${formatBytes(bytesPerSecond)}/s`;
};

/**
 * Rounded, human duration. Deliberately coarse: a countdown that ticks every
 * second reads as more precise than an upload estimate can honestly be.
 */
export const formatDuration = (milliseconds: number): string => {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return "-";

  const totalSeconds = Math.ceil(milliseconds / 1000);
  if (totalSeconds < 5) return "enkele seconden";
  if (totalSeconds < 60) return `${Math.ceil(totalSeconds / 5) * 5} seconden`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes < 5) {
    // Under 5 minutes the seconds still carry information.
    const roundedSeconds = Math.round(seconds / 10) * 10;
    if (roundedSeconds === 0 || roundedSeconds === 60) {
      return `${minutes + (roundedSeconds === 60 ? 1 : 0)} min`;
    }
    return `${minutes} min ${roundedSeconds} s`;
  }

  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0
    ? `${hours} uur`
    : `${hours} uur ${remainingMinutes} min`;
};

/** "Nog ongeveer 2 min 10 s" / "Bijna klaar" */
export const formatRemaining = (milliseconds: number | null): string => {
  if (milliseconds === null) return "Resterende tijd wordt berekend...";
  if (milliseconds <= 3000) return "Bijna klaar";
  return `Nog ongeveer ${formatDuration(milliseconds)}`;
};
